import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, Typography, Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { postWarehouseItemsViaFile } from 'entities/Warehouse/api';
import { ColorPalette } from 'shared/consts/colorPalette';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Paper } from 'shared/ui/Paper';
import { SheetTable } from 'shared/ui/SheetTable';
import { DnDImportFiles } from 'shared/ui/DndImportFiles';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { Loader } from 'shared/ui/Loader';

interface ImportCsvModalPropsType {
	open: boolean;
	handleClose: () => void;
	setIsOpenInfoModal: () => void;
}

export const ImportCsvModal: FC<ImportCsvModalPropsType> = ({ open, handleClose, setIsOpenInfoModal }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [file, setFile] = useState<File>();

	const sheetPreviewArr = [
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.article_code') },
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.article_description') },
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.ean_code') },
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.code_from_document') },
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.description_from_document') },
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.partner_vat_number') },
		{ cellContent: t('extensions.ex_list.warehouse.csv_cells.partner_vat_code') },
	];

	const { mutate: postViaFileMutate, isLoading: postIsLoading } = useMutation({
		mutationKey: ['post/warehouse-via-csv'],
		mutationFn: postWarehouseItemsViaFile,
	});

	const uploadCsvHandler = () => {
		if (file) {
			postViaFileMutate(
				{ file },
				{
					onSuccess: () => {
						queryClient.refetchQueries({ queryKey: ['get/warehouse-items'] });
						enqueueSnackbar({
							message: t('extensions.snackbar.success_warehouse_creation'),
							variant: 'success',
						});
						setIsOpenInfoModal();
						handleClose();
						setFile(undefined);
					},
					onError: (err) => {
						showErrorNotification(err as AxiosError);
						setFile(undefined);
					},
				},
			);
		}
	};

	const uploadedFilesHandler = (files: Array<File>) => {
		if (files && files?.length > 1) {
			enqueueSnackbar({ message: t('shared_texts.import_csv_file_only_one'), variant: 'error' });
		} else if (files) {
			setFile(files[0]);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => {
				handleClose();
				setFile(undefined);
			}}
			PaperComponent={Paper}
			PaperProps={{ title: t('shared_texts.csv_modal_title') as string }}
			sx={{
				'width': '65vw',
				'margin': '0 auto',
				'& .MuiPaper-root': {
					minWidth: '100%',
				},
			}}
		>
			{postIsLoading ? (
				<Loader />
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
					<Typography variant={'body1'} color={ColorPalette.baliHai}>
						{t('extensions.modals.warehouse_import_csv_creation_desctiption_1')}
					</Typography>

					<DnDImportFiles
						setUploadedFiles={uploadedFilesHandler}
						fileDescription={t('partners.modals.import_csv_file_type')}
						uploaded={Boolean(file)}
					/>

					<SheetTable cells={sheetPreviewArr} />

					<Typography variant={'body1'} color={ColorPalette.baliHai}>
						{t('extensions.modals.warehouse_import_csv_creation_desctiption_2')}
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Box sx={{ width: '400px' }}>
							<ActionsForDialog
								cancelLabel={t('shared_texts.cancel')}
								cancelAction={handleClose}
								applyLabel={t('shared_texts.save')}
								applyAction={uploadCsvHandler}
							/>
						</Box>
					</Box>
				</Box>
			)}
		</Dialog>
	);
};
